// The side list: this page's pins whose element isn't on screen (another view, a closed drawer, a
// changed layout). Each opens its thread.
import { X } from "lucide-react";
import { FEEDBACK_UI_ATTR } from "../../lib/feedback/anchor";
import type { Pin } from "../../lib/feedback/api";
import { cx } from "../ui";
import { StatusChip, when } from "./parts";

export function excerpt(note: string, max = 90): string {
  const t = note.replace(/\s+/g, " ").trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

export function PinList({ pins, activeId, onOpen, onClose, notice }: { pins: Pin[]; activeId: string | null; onOpen: (id: string) => void; onClose: () => void; notice?: string }) {
  return (
    <aside
      {...{ [FEEDBACK_UI_ATTR]: "" }}
      aria-label="Pins not on screen"
      className="pointer-events-auto fixed inset-x-0 bottom-0 z-[79] max-h-[60vh] overflow-y-auto rounded-t-[12px] border border-line bg-raised text-ink shadow-[var(--shadow-2)] sm:inset-x-auto sm:top-[72px] sm:bottom-auto sm:left-3 sm:max-h-[calc(100vh-96px)] sm:w-[320px] sm:rounded-[10px]"
    >
      <div className="flex items-center justify-between border-b border-subtle px-3 py-1">
        <h2 className="eyebrow text-ink-2">Not on screen ({pins.length})</h2>
        <button type="button" onClick={onClose} aria-label="Close list" className="focus-ring inline-flex h-11 w-11 items-center justify-center rounded-md text-ink-2 hover:bg-hover">
          <X size={18} aria-hidden />
        </button>
      </div>
      {notice ? <p role="status" className="px-3 py-2 text-[13px] text-ink-2">{notice}</p> : null}
      {pins.length ? (
        <ul className="divide-y divide-subtle">
          {pins.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => onOpen(p.id)}
                aria-current={p.id === activeId || undefined}
                className={cx("focus-ring flex min-h-11 w-full flex-col items-start gap-1 px-3 py-2 text-left hover:bg-hover", p.id === activeId && "bg-selected")}
              >
                <span className="flex w-full items-center gap-2">
                  <span className="text-[13px] font-semibold text-ink">Pin {p.number}</span>
                  <StatusChip status={p.status} />
                  <span className="ml-auto text-[12px] text-ink-3">{when(p.created_at)}</span>
                </span>
                <span className="text-[13px] text-ink-2">{excerpt(p.note)}</span>
                <span className="text-[12px] text-ink-3">{p.author_name}{p.reply_count ? ` · ${p.reply_count} repl${p.reply_count === 1 ? "y" : "ies"}` : ""}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : !notice ? (
        <p className="px-3 py-3 text-[13px] text-ink-3">Every pin on this page is on screen.</p>
      ) : null}
    </aside>
  );
}
